import { useState, useEffect, useCallback } from "react";
import { callAPI } from "../../../axios";
import { getChainIdLS } from "../../../utils/utils";
import {
  normaliseTemplateSearchResponse,
  normaliseTemplateList,
} from "./welcomeMessageTemplateUtils";

const fetchTemplateSearch = (chainIdValue, pageNo, recordsPerPage) => {
  const apiPath = `/welcome-message-template-search?chainId=${encodeURIComponent(
    chainIdValue
  )}&pageNo=${pageNo}&recordsPerPage=${recordsPerPage}`;
  return callAPI(apiPath, "GET", undefined, { chainId: chainIdValue });
};

const fetchAllTemplatesByChain = (chainIdValue) => {
  const apiPath = `/welcome-message-template?chainId=${encodeURIComponent(chainIdValue)}`;
  return callAPI(apiPath, "GET", undefined, { chainId: chainIdValue });
};

const EMPTY_PAGINATED_RESPONSE = {
  welcomeMessageResponse: [],
  totalPages: 1,
  totalRecords: 0,
};

/**
 * Shared hook for welcome message templates.
 * - Paginated search for Default Welcome Message table
 * - GET /welcome-message-template?chainId=… for full list (Custom room lookup)
 */
export function useWelcomeMessageTemplates(
  selectedChainId,
  listPageNo = 1,
  listRecordsPerPage = 10
) {
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [welcomeMessageList, setWelcomeMessageList] = useState({});
  const [totalPages, setTotalPages] = useState(1);

  const resolveChainId = useCallback(
    () => selectedChainId || getChainIdLS(),
    [selectedChainId]
  );

  const loadPaginatedList = useCallback(() => {
    const chainIdValue = resolveChainId();
    if (!chainIdValue) {
      setWelcomeMessageList(EMPTY_PAGINATED_RESPONSE);
      setTotalPages(1);
      setLoading(false);
      return Promise.resolve(EMPTY_PAGINATED_RESPONSE);
    }

    setLoading(true);
    return fetchTemplateSearch(chainIdValue, listPageNo, listRecordsPerPage)
      .then((apiResponse) => {
        const response =
          apiResponse == null
            ? EMPTY_PAGINATED_RESPONSE
            : normaliseTemplateSearchResponse(apiResponse);
        setWelcomeMessageList(response);
        setTotalPages(response?.totalPages || 1);
        setLoading(false);
        return response;
      })
      .catch((err) => {
        console.warn(err?.message || err);
        setWelcomeMessageList(EMPTY_PAGINATED_RESPONSE);
        setTotalPages(1);
        setLoading(false);
        return EMPTY_PAGINATED_RESPONSE;
      });
  }, [resolveChainId, listPageNo, listRecordsPerPage]);

  const loadAllTemplates = useCallback(() => {
    const chainIdValue = resolveChainId();
    if (!chainIdValue) {
      setTemplates([]);
      return Promise.resolve([]);
    }

    return fetchAllTemplatesByChain(chainIdValue)
      .then((apiResponse) => {
        const rows = apiResponse == null ? [] : normaliseTemplateList(apiResponse);
        setTemplates(rows);
        return rows;
      })
      .catch((err) => {
        console.warn(err?.message || err);
        setTemplates([]);
        return [];
      });
  }, [resolveChainId]);

  useEffect(() => {
    loadPaginatedList();
  }, [loadPaginatedList]);

  useEffect(() => {
    loadAllTemplates();
  }, [loadAllTemplates]);

  const refetchList = useCallback(() => {
    loadPaginatedList();
    return loadAllTemplates();
  }, [loadPaginatedList, loadAllTemplates]);

  return {
    templates,
    welcomeMessageList,
    totalPages,
    loading,
    refetchList,
    loadAllTemplates,
  };
}
